import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import type { PublicOcrNumber } from '@/types/_apiHidden'
import type { OcrNumber } from '@/types/ocr-numbers'
import type { SettlementDocument } from '@/types/settlement-documents'
import { toPayNow } from '@/utils/debtStage'

/**
 * THE single place that decides what the payment provider is asked to collect (ADA-4432 D1).
 *
 * Replaces the legacy `payments/index.vue` `payment_amount_and_currency`, which picked between
 * the document's live remaining amount, the amortization letter's `print.amount` and the frozen
 * `ocr.amount` — the last two being the "frozen letter amount" ADA-4432 removed.
 *
 * The rule, by context:
 *  - a settlement document → `toPayNow`, the materialized debt, matching the card the payer
 *    came from.
 *  - an amortization advice → the advice amount EXACTLY (D12). The advice amount is already
 *    materialized, and interest belongs to the plan as a whole, never to a single advice.
 *  - the logged-out OCR lookup → the server's `amount`, which follows the same rule.
 *
 * ADA-4445 (PO decision 2026-08-10) removed the preliminary interest that used to be added on
 * top of `toPayNow` here: only booked transactions are shown and charged. The accepted
 * consequence is that interest accrued since the last materialization is collected afterwards,
 * as it was before the ADA-4372 package.
 */

export type PayableSource =
  /** Signed in: the document the payer opened the dialog from. `ocr` carries the amortization
   *  override the cards already apply (advice amount instead of the plan-level OCR). */
  | { kind: 'document'; document: SettlementDocument; ocr?: OcrNumber }
  /** Signed out: the resolved public lookup from the OCR step. */
  | { kind: 'public'; lookup: PublicOcrNumber }

export function usePayableAmount(source: MaybeRefOrGetter<PayableSource | null>) {
  const current = computed(() => toValue(source))
  const isAmortization = computed(
    () => current.value?.kind === 'document' && current.value.document.reference === 'amortization'
  )

  const amount = computed(() => {
    const payable = current.value
    if (payable == null) return 0
    if (payable.kind === 'public') return payable.lookup.amount
    // The advice amount is mirrored onto `ocr.amount` by the cards; the plan-level OCR is not
    // what the customer pays.
    if (isAmortization.value) return payable.ocr?.amount ?? 0
    return toPayNow(payable.document)
  })

  const currency = computed(() => {
    const payable = current.value
    if (payable == null) return undefined
    if (payable.kind === 'public') return payable.lookup.currency ?? undefined
    return payable.document.currency ?? payable.ocr?.currency ?? undefined
  })

  return { amount, currency }
}
